import { durataStringaInSecondi, pulisciNome, secondiInHHMMSS } from "./parseReportPresenze";

const PAROLE_BOT_IF = ["virtual meeting assistant", "managing"];

function isSezioneSuccessiva(riga) {
  return /^\d+\./.test((riga?.[0] || "").trim());
}

function isNomeGenericoIF(nome) {
  if (!nome) return true;
  const pulito = nome.replace(/\(non verificato\)/gi, "").trim();
  const tokens = pulito.split(/\s+/).filter(Boolean);
  return tokens.length <= 1;
}

function normalizzaNomeIF(nome) {
  if (!nome) return "";
  return nome.replace(/\(.*?\)/g, "").trim().toLowerCase().replace(/\s+/g, " ");
}

// Converte "5/20/26, 8:34:00 AM" (formato Teams, M/D/YY h:mm:ss AM/PM) in Date
export function parseTimestampTeams(stringa) {
  if (!stringa) return null;
  const match = String(stringa).trim().match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{2,4}),?\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i
  );
  if (!match) return null;
  const [, mm, dd, yy, hh12, min, sec, ampm] = match;
  const anno = yy.length === 2 ? 2000 + parseInt(yy, 10) : parseInt(yy, 10);
  let ore = parseInt(hh12, 10) % 12;
  if (ampm.toUpperCase() === "PM") ore += 12;
  const data = new Date(anno, parseInt(mm, 10) - 1, parseInt(dd, 10), ore, parseInt(min, 10), parseInt(sec, 10));
  return isNaN(data.getTime()) ? null : data;
}

function trovaHeaderPartecipantiIF(righe) {
  for (let i = 0; i < righe.length; i++) {
    const riga = righe[i];
    if (riga[0] === "Nome" && riga.includes("Primo accesso") && riga.includes("Ultima uscita")) {
      return i;
    }
  }
  return -1;
}

function leggiDurataMaxRiunioneIF(righe) {
  const limite = Math.min(righe.length, 10);
  for (let i = 0; i < limite; i++) {
    if (righe[i][0] === "Durata della riunione") {
      const durata = durataStringaInSecondi(righe[i][1]);
      if (durata > 0) return durata;
    }
  }
  // Fallback: differenza tra Ora di inizio e Ora di fine
  let inizio = null;
  let fine = null;
  for (let i = 0; i < limite; i++) {
    if (righe[i][0] === "Ora di inizio") inizio = parseTimestampTeams(righe[i][1]);
    if (righe[i][0] === "Ora di fine") fine = parseTimestampTeams(righe[i][1]);
  }
  if (inizio && fine) {
    const diff = Math.round((fine - inizio) / 1000);
    if (diff > 0) return diff;
  }
  return null;
}

// Fase 1 — pulizia/deduplica del CSV Teams (equivalente alla skill pulizia-report-presenze-teams)
export async function parseTeamsPresenzaIF(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const buffer = e.target.result;
        const decoder = new TextDecoder("utf-16le");
        let testo = decoder.decode(buffer);
        if (testo.charCodeAt(0) === 0xFEFF) testo = testo.slice(1);

        const righe = testo.split(/\r?\n/).map((r) => r.split("\t"));

        const durataMaxSecondi = leggiDurataMaxRiunioneIF(righe);
        const headerIdx = trovaHeaderPartecipantiIF(righe);
        if (headerIdx < 0) {
          reject(new Error("Sezione partecipanti non trovata nel file CSV"));
          return;
        }

        const header = righe[headerIdx];
        const colNome = header.indexOf("Nome");
        const colPrimoAccesso = header.indexOf("Primo accesso");
        const colUltimaUscita = header.indexOf("Ultima uscita");
        const colEmail = header.indexOf("Posta elettronica");
        const colRuolo = header.indexOf("Ruolo");

        const gruppi = {};

        for (let i = headerIdx + 1; i < righe.length; i++) {
          const riga = righe[i];
          if (!riga) continue;
          if (isSezioneSuccessiva(riga)) break;
          if (riga.length < 3) continue;

          const nome = riga[colNome]?.trim() || "";
          if (!nome || nome === "Nome") continue;
          if (PAROLE_BOT_IF.some((kw) => nome.toLowerCase().includes(kw))) continue;

          const email = riga[colEmail]?.trim()?.toLowerCase() || "";
          if (!email && isNomeGenericoIF(nome)) continue;

          const ingresso = parseTimestampTeams(riga[colPrimoAccesso]);
          const uscita = parseTimestampTeams(riga[colUltimaUscita]);
          const secondiRiga = ingresso && uscita ? Math.max(0, Math.round((uscita - ingresso) / 1000)) : 0;
          const ruolo = colRuolo >= 0 ? riga[colRuolo]?.trim() || "" : "";

          const chiave = email || normalizzaNomeIF(nome);
          if (!chiave) continue;

          if (gruppi[chiave]) {
            const g = gruppi[chiave];
            g.secondiTotali += secondiRiga;
            if (nome.length > g._nomeOriginale.length) g._nomeOriginale = nome;
            if (!g.email && email) g.email = email;
            if (!g.ruolo && ruolo) g.ruolo = ruolo;
            if (ingresso && (!g.primoAccesso || ingresso < g.primoAccesso)) g.primoAccesso = ingresso;
            if (uscita && (!g.ultimaUscita || uscita > g.ultimaUscita)) g.ultimaUscita = uscita;
          } else {
            gruppi[chiave] = {
              _nomeOriginale: nome,
              email,
              ruolo,
              secondiTotali: secondiRiga,
              primoAccesso: ingresso || null,
              ultimaUscita: uscita || null,
            };
          }
        }

        const partecipanti = Object.values(gruppi).map((g) => ({
          nome: pulisciNome(g._nomeOriginale),
          email: g.email,
          ruolo: g.ruolo,
          primoAccesso: g.primoAccesso,
          ultimaUscita: g.ultimaUscita,
          secondiTotali: durataMaxSecondi ? Math.min(g.secondiTotali, durataMaxSecondi) : g.secondiTotali,
        }));

        resolve({ partecipanti, durataMaxSecondi });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// Fase 2 — confronto con il file iscritti: split nome/cognome + arricchimento email
function normalizzaChiaveMatch(s) {
  if (!s) return "";
  return String(s).toLowerCase().trim().replace(/['`.]/g, "").replace(/\s+/g, " ");
}

function titleCase(s) {
  if (!s) return "";
  return String(s).trim().toLowerCase().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
}

export function matchIscrittiPresenza(partecipanti, iscritti) {
  const lookup = {};
  iscritti.forEach((isc) => {
    const first = isc["First Name"] || "";
    const last = isc["Last Name"] || "";
    const keyFn = normalizzaChiaveMatch(`${first} ${last}`);
    const keyNf = normalizzaChiaveMatch(`${last} ${first}`);
    if (keyFn) lookup[keyFn] = isc;
    if (keyNf) lookup[keyNf] = isc;
  });

  function trovaMatch(key) {
    if (lookup[key]) return lookup[key];
    const tokens = key.split(" ").filter(Boolean);
    if (tokens.length < 2) return null;
    const tokenSet = new Set(tokens);
    for (const lk of Object.keys(lookup)) {
      const lkTokens = new Set(lk.split(" ").filter(Boolean));
      const sottoinsieme = [...tokenSet].every((t) => lkTokens.has(t)) ||
        [...lkTokens].every((t) => tokenSet.has(t));
      if (sottoinsieme) return lookup[lk];
    }
    return null;
  }

  return partecipanti.map((p) => {
    const key = normalizzaChiaveMatch(p.nome);
    const match = trovaMatch(key);

    let nome, cognome;
    if (match) {
      nome = titleCase(match["First Name"]);
      cognome = titleCase(match["Last Name"]);
    } else {
      const parti = p.nome.split(/\s+/).filter(Boolean);
      nome = parti[0] || "";
      cognome = parti.slice(1).join(" ");
    }

    const email = p.email || (match && match["Email"] ? String(match["Email"]).trim().toLowerCase() : "");

    return { ...p, nome, cognome, email };
  });
}

// Fase 3 — deduplica tentativi test LMS (equivalente alla skill deduplica-test-lms)
export function dedupeTestLMS(record) {
  const byEmail = new Map();
  const conteggi = new Map();

  record.forEach((r) => {
    const email = String(r["Student Email"] || "").trim().toLowerCase();
    if (!email) return;
    conteggi.set(email, (conteggi.get(email) || 0) + 1);

    const puntiCorrenti = Number(r["Total Correct"]);
    const esistente = byEmail.get(email);
    if (!esistente || (Number.isFinite(puntiCorrenti) && puntiCorrenti > esistente._punti)) {
      byEmail.set(email, {
        email,
        totalCorrect: r["Total Correct"],
        dateCompleted: r["Date Completed (UTC)"],
        _punti: Number.isFinite(puntiCorrenti) ? puntiCorrenti : -Infinity,
      });
    }
  });

  let partecipantiConPiuTentativi = 0;
  byEmail.forEach((v, email) => {
    v.tentativi = conteggi.get(email) || 1;
    if (v.tentativi > 1) partecipantiConPiuTentativi++;
  });

  return {
    byEmail,
    summary: {
      partecipantiUnici: byEmail.size,
      tentativiTotali: record.length,
      partecipantiConPiuTentativi,
    },
  };
}

// Fase 4 — merge finale iscritti + presenza + test (equivalente alla skill report-lezione-completo)
export function mergeReportLezioneCompleto(iscritti, partecipantiArricchiti, testDedup) {
  const presenzaByEmail = new Map();
  partecipantiArricchiti.forEach((p) => {
    if (p.email) presenzaByEmail.set(p.email, p);
  });

  const rows = iscritti.map((isc) => {
    const email = String(isc["Email"] || "").trim().toLowerCase();
    const presenza = presenzaByEmail.get(email);
    const test = testDedup.byEmail.get(email);

    return {
      firstName: isc["First Name"] || "",
      lastName: isc["Last Name"] || "",
      email: isc["Email"] || "",
      presenza: presenza ? secondiInHHMMSS(presenza.secondiTotali) : null,
      presenzaSecondi: presenza ? presenza.secondiTotali : null,
      risposteCorrette: test && test.totalCorrect != null ? test.totalCorrect : null,
      dataEsecuzioneTest: test && test.dateCompleted != null ? test.dateCompleted : null,
      tentativiTest: test ? test.tentativi : null,
    };
  });

  return {
    rows,
    summary: {
      iscrittiTotali: rows.length,
      presenti: rows.filter((r) => r.presenzaSecondi != null).length,
      testCompletati: rows.filter((r) => r.risposteCorrette != null).length,
    },
  };
}
